import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleHelp,
  Clock3,
  Copy,
  Gauge,
  ListChecks,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  ToggleLeft,
  TrendingUp,
} from "lucide-react";
import { api, ApiError } from "./api.ts";
import type { Scope, StrategyVersion, SystemInfo } from "./types.ts";

type NoticeSetter = (notice: { type: "error" | "success"; text: string } | null) => void;

const ROLE_NAMES = ["RANK", "EXACT", "AUTO", "BROAD", "PHRASE", "COMPETITOR", "WINNER"] as const;

const SETTINGS_DEFAULTS = {
  decision_thresholds: {
    zero_order_clicks: 20,
    zero_order_spend_multiplier_of_hard_cpa: 1,
    min_orders_for_bid_change: 2,
    min_orders_for_harvest: 2,
    min_clicks_for_trend: 10,
    prior_cvr: null,
    prior_clicks: 20,
    material_acos_change: 0.1,
    material_cpa_change: 0.1,
    material_order_change: 0.15,
    material_rank_positions: 3,
  },
  bid_guardrails: {
    default_floor: 0.02,
    default_ceiling: 1.5,
    max_increase_percent: 0.15,
    max_decrease_percent: 0.2,
    max_absolute_change: 1,
    harvest_bid_ceiling: 1.2,
  },
  harvest_destinations: {
    create_if_missing: true,
    campaign_daily_budget: 10,
    new_ad_group_name: "ASIN",
    new_ad_group_default_bid_source: "BID_FLOOR",
  },
  batch_guardrails: {
    max_actions: 30,
    max_actions_per_campaign: 15,
    max_negative_exact: 50,
    max_negative_phrase: 5,
    max_negative_asin: 50,
    max_projected_weekly_spend_increase: 100,
  },
  observation: {
    excluded_reporting_lag_days: 3,
    earliest_review_days: 4,
    mature_review_days: 10,
    max_extension_days: 7,
  },
  role_overrides: Object.fromEntries(ROLE_NAMES.map((role) => [role, { target_acos_multiplier: role === "RANK" ? 1.3 : 1, target_cpa_multiplier: role === "RANK" ? 1.3 : 1 }])),
  protected: { brand_terms: [], core_terms: [], asins: [] },
  features: {
    allow_acos_driven_actions: true,
    allow_keyword_harvest: true,
    allow_asin_harvest: true,
    allow_negative_exact: true,
    allow_negative_phrase: true,
    allow_negative_asin: true,
    allow_self_asin_cross_sell: false,
    allow_campaign_pause: false,
    allow_budget_changes: true,
    allow_placement_changes: true,
  },
};

const cloneDefaults = () => structuredClone(SETTINGS_DEFAULTS);

function mergeSettings(value: any): any {
  const defaults = cloneDefaults();
  return {
    ...value,
    decision_thresholds: { ...defaults.decision_thresholds, ...(value?.decision_thresholds ?? {}) },
    bid_guardrails: { ...defaults.bid_guardrails, ...(value?.bid_guardrails ?? {}) },
    harvest_destinations: { ...defaults.harvest_destinations, ...(value?.harvest_destinations ?? {}) },
    batch_guardrails: { ...defaults.batch_guardrails, ...(value?.batch_guardrails ?? {}) },
    observation: { ...defaults.observation, ...(value?.observation ?? {}) },
    role_overrides: Object.fromEntries(
      ROLE_NAMES.map((role) => [role, { ...defaults.role_overrides[role], ...(value?.role_overrides?.[role] ?? {}) }]),
    ),
    protected: { ...defaults.protected, ...(value?.protected ?? {}) },
    features: { ...defaults.features, ...(value?.features ?? {}) },
  };
}

function pathValue(value: any, path: string): any {
  return path.split(".").reduce((current, key) => current?.[key], value);
}

function updatePath(value: any, path: string, next: unknown): any {
  const clone = structuredClone(value);
  const parts = path.split(".");
  let cursor = clone;
  for (const part of parts.slice(0, -1)) cursor = cursor[part];
  cursor[parts.at(-1)!] = next;
  return clone;
}

function parseVersionStrategy(version: StrategyVersion): any {
  return mergeSettings(JSON.parse(version.canonical_json ?? "{}"));
}

function showApiError(error: unknown, setNotice: NoticeSetter) {
  if (error instanceof ApiError) setNotice({ type: "error", text: error.message });
  else setNotice({ type: "error", text: error instanceof Error ? error.message : "操作失败，请稍后重试" });
}

export function StrategySettingsPage({
  scopes,
  selected,
  onSelect,
  onChanged,
  setNotice,
  system,
  isRefreshing,
  onToggleMenu,
  embedded = false,
}: {
  scopes: Scope[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  onChanged: () => Promise<void>;
  setNotice: NoticeSetter;
  system: SystemInfo | null;
  isRefreshing?: boolean;
  onToggleMenu?: () => void;
  embedded?: boolean;
}) {
  const [active, setActive] = useState<StrategyVersion | null>(null);
  const [versions, setVersions] = useState<StrategyVersion[]>([]);
  const [strategy, setStrategy] = useState<any>(null);
  const [validation, setValidation] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const isReadOnly = system?.write_kill_switch ?? true;
  const scope = scopes.find((item) => item.scope_id === selected) ?? scopes[0];

  useEffect(() => {
    if (scope && scope.scope_id !== selected) onSelect(scope.scope_id);
  }, [scope?.scope_id, selected, onSelect]);

  const load = useCallback(async () => {
    if (!scope) {
      setVersions([]);
      setActive(null);
      setStrategy(null);
      return;
    }
    try {
      setBusy(true);
      const data = await api.get<StrategyVersion[]>(`/api/v1/scopes/${scope.scope_id}/strategies`);
      setVersions(data);
      const candidate = data.find((version) => version.status === "DRAFT") ?? data.find((version) => version.status === "VALIDATED") ?? data[0];
      if (!candidate) {
        setActive(null);
        setStrategy(null);
        setValidation(null);
        return;
      }
      const detail = await api.get<StrategyVersion>(`/api/v1/strategy-versions/${candidate.version_id}`);
      setActive(detail);
      setStrategy(parseVersionStrategy(detail));
      setValidation(detail.validation_result_json ? JSON.parse(detail.validation_result_json) : null);
    } catch (error) {
      showApiError(error, setNotice);
    } finally {
      setBusy(false);
    }
  }, [scope?.scope_id, setNotice]);

  useEffect(() => {
    void load();
  }, [load]);

  const set = (path: string, value: unknown) => {
    setStrategy((current: any) => (current ? updatePath(current, path, value) : current));
    setValidation(null);
  };

  const persist = async () => {
    if (!active || !strategy) return null;
    const updated = await api.put<StrategyVersion>(`/api/v1/strategy-versions/${active.version_id}`, {
      strategy,
      change_note: active.change_note,
    });
    setActive(updated);
    setStrategy(parseVersionStrategy(updated));
    setVersions((current) => current.map((version) => (version.version_id === updated.version_id ? updated : version)));
    return updated;
  };

  const createDraft = async () => {
    if (!scope) return;
    try {
      setBusy(true);
      await api.post(`/api/v1/scopes/${scope.scope_id}/strategies/drafts`, {
        change_note: versions.length ? "策略参数修订" : "初始策略参数",
        strategy: strategy ?? undefined,
      });
      setNotice({ type: "success", text: "已创建策略参数草稿" });
      await load();
    } catch (error) {
      showApiError(error, setNotice);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    try {
      setBusy(true);
      await persist();
      setNotice({ type: "success", text: "策略参数草稿已保存" });
    } catch (error) {
      showApiError(error, setNotice);
    } finally {
      setBusy(false);
    }
  };

  const validate = async () => {
    if (!active || !strategy) return;
    try {
      setBusy(true);
      const persisted = await persist();
      if (!persisted) return;
      const result = await api.post<any>(`/api/v1/strategy-versions/${persisted.version_id}/validate`);
      setValidation(result);
      if (result.version) {
        setActive(result.version);
        setStrategy(parseVersionStrategy(result.version));
      }
      setNotice({ type: result.valid ? "success" : "error", text: result.valid ? "策略参数校验通过" : `校验发现 ${result.errors?.length ?? 0} 项错误` });
    } catch (error) {
      showApiError(error, setNotice);
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!active) return;
    try {
      setBusy(true);
      await api.post(`/api/v1/strategy-versions/${active.version_id}/publish`);
      setNotice({ type: "success", text: `策略版本 v${active.version} 已发布` });
      await load();
      await onChanged();
    } catch (error) {
      showApiError(error, setNotice);
    } finally {
      setBusy(false);
    }
  };

  const isDraft = active?.status === "DRAFT";
  const isValidated = active?.status === "VALIDATED";
  const canEdit = Boolean(!isReadOnly && isDraft && strategy);
  const statusLabel = active?.status === "PUBLISHED" ? "已发布" : active?.status === "VALIDATED" ? "已校验" : active?.status === "DRAFT" ? "草稿" : active?.status ?? "未创建";

  return (
    <>
      {!embedded && <div className="unified-page-header">
        <div className="header-title-group">
          {onToggleMenu && <button type="button" className="icon-button mobile-menu overview-mobile-menu" onClick={onToggleMenu} title="切换导航菜单" aria-label="切换导航菜单"><SlidersHorizontal /></button>}
          <div className="title-with-badge">
            <h1>优化策略</h1>
            <span className="page-subtitle">调整广告优化决策阈值与执行护栏</span>
          </div>
        </div>
        <div className="header-action-group">
          {scope && <span className="strategy-scope-badge"><Target size={13} /> {scope.child_asin}</span>}
          <button type="button" className="header-refresh-btn" onClick={() => void load()} disabled={isRefreshing || busy} title="刷新优化策略" aria-label="刷新优化策略"><RefreshCw size={14} className={isRefreshing || busy ? "spin" : ""} /></button>
        </div>
      </div>}

      {!scopes.length ? (
        <section className="empty-state strategy-empty-state"><SlidersHorizontal /><h2>尚未创建 ASIN Scope</h2><p>先在 ASIN 运营中创建并校验一个 ASIN，才能配置对应的优化策略。</p></section>
      ) : (
        <div className={`strategy-settings-layout ${embedded ? "embedded" : ""}`}>
          {!embedded && <aside className="strategy-scope-panel">
            <div className="strategy-panel-heading"><div><strong>ASIN 策略</strong><span>{scopes.length} 个运营对象</span></div><Target size={18} /></div>
            <div className="strategy-scope-list">
              {scopes.map((item) => (
                <button key={item.scope_id} type="button" className={item.scope_id === scope?.scope_id ? "strategy-scope-item active" : "strategy-scope-item"} onClick={() => onSelect(item.scope_id)}>
                  <span className="strategy-scope-icon">{item.marketplace}</span>
                  <span><strong>{item.child_asin}</strong><small>{item.display_name || item.sku}</small></span>
                  <ChevronRight size={15} />
                </button>
              ))}
            </div>
          </aside>}

          <div className="strategy-settings-main">
            {!scope ? <LoadingSettings /> : !strategy || !active ? (
              <section className="empty-state strategy-empty-state"><SlidersHorizontal /><h2>尚未创建策略参数</h2><p>为 {scope.child_asin} 创建第一版策略草稿，开始设置决策阈值和广告护栏。</p><button className="primary-button" onClick={() => void createDraft()} disabled={busy || isReadOnly || scope.status !== "ACTIVE"} title={scope.status !== "ACTIVE" ? "请先完成 ASIN 身份校验" : isReadOnly ? "解除写入锁后可创建草稿" : "创建策略参数草稿"}><Plus /> 创建策略草稿</button></section>
            ) : (
              <>
                <div className="strategy-version-bar">
                  <div className="strategy-version-meta"><strong>策略版本 v{active.version}</strong><span className={`strategy-status ${active.status.toLowerCase()}`}>{statusLabel}</span><code>{active.strategy_hash.slice(0, 12)}</code></div>
                  <div className="strategy-version-actions">
                    <button className="secondary-button" onClick={() => void createDraft()} disabled={busy || isReadOnly || isDraft} title={isDraft ? "当前已有草稿版本" : "基于当前版本创建新的策略草稿"}><Copy size={14} /> 新建版本</button>
                    <button className="secondary-button" onClick={() => void validate()} disabled={busy || !canEdit} title={!isDraft ? "只有草稿可以校验" : "校验当前策略草稿"}><Check size={14} /> 校验</button>
                    <button className={isValidated && !isReadOnly ? "primary-button" : "secondary-button"} onClick={() => void publish()} disabled={busy || isReadOnly || !isValidated} title={!isValidated ? "请先通过校验" : "发布策略版本"}><ShieldCheck size={14} /> 发布</button>
                  </div>
                </div>
                {!isDraft && <div className="strategy-version-note"><CircleHelp size={15} /> 当前版本为{active.status === "PUBLISHED" ? "线上生效版本" : "已校验版本"}。如需调整，请先点击“新建版本”。</div>}

                <div className="strategy-form-intro"><div><h2>{scope.child_asin} 的优化策略 <HelpTip text="这些参数会影响每周优化的出价、收割、否定与批量变更决策。修改后请先校验，再发布。" /></h2></div><span className="strategy-edit-state">{canEdit ? <><CheckCircle2 size={14} /> 可编辑</> : <><Clock3 size={14} /> 只读</>}</span></div>
                {validation && <ValidationSummary validation={validation} />}

                <StrategySection icon={<Gauge />} title="决策阈值" description="定义系统何时认为数据足够，何时触发出价和趋势判断。">
                  <div className="strategy-form-grid">
                    <NumberField label="零订单点击数" hint="达到后可触发零订单处理" path="decision_thresholds.zero_order_clicks" strategy={strategy} set={set} disabled={!canEdit} integer />
                    <NumberField label="零订单花费 / Hard CPA 倍数" path="decision_thresholds.zero_order_spend_multiplier_of_hard_cpa" strategy={strategy} set={set} disabled={!canEdit} step="0.1" />
                    <NumberField label="出价调整最少订单" path="decision_thresholds.min_orders_for_bid_change" strategy={strategy} set={set} disabled={!canEdit} integer />
                    <NumberField label="收割最少订单" path="decision_thresholds.min_orders_for_harvest" strategy={strategy} set={set} disabled={!canEdit} integer />
                    <NumberField label="趋势判断最少点击" path="decision_thresholds.min_clicks_for_trend" strategy={strategy} set={set} disabled={!canEdit} integer />
                    <PercentField label="先验 CVR" hint="留空表示不使用先验" path="decision_thresholds.prior_cvr" strategy={strategy} set={set} disabled={!canEdit} nullable />
                    <NumberField label="先验点击数" path="decision_thresholds.prior_clicks" strategy={strategy} set={set} disabled={!canEdit} integer />
                    <PercentField label="ACOS 显著变化" path="decision_thresholds.material_acos_change" strategy={strategy} set={set} disabled={!canEdit} />
                    <PercentField label="CPA 显著变化" path="decision_thresholds.material_cpa_change" strategy={strategy} set={set} disabled={!canEdit} />
                    <PercentField label="订单显著变化" path="decision_thresholds.material_order_change" strategy={strategy} set={set} disabled={!canEdit} />
                    <NumberField label="排名显著变化位数" path="decision_thresholds.material_rank_positions" strategy={strategy} set={set} disabled={!canEdit} integer />
                  </div>
                </StrategySection>

                <StrategySection icon={<TrendingUp />} title="出价护栏" description={`控制单次出价调整范围，金额单位为 ${scope.currency}。`}>
                  <div className="strategy-form-grid">
                    <NumberField label="默认出价下限" path="bid_guardrails.default_floor" strategy={strategy} set={set} disabled={!canEdit} min="0.02" step="0.01" suffix={scope.currency} />
                    <NumberField label="默认出价上限" path="bid_guardrails.default_ceiling" strategy={strategy} set={set} disabled={!canEdit} step="0.01" suffix={scope.currency} />
                    <PercentField label="最大单次上调" path="bid_guardrails.max_increase_percent" strategy={strategy} set={set} disabled={!canEdit} />
                    <PercentField label="最大单次下调" path="bid_guardrails.max_decrease_percent" strategy={strategy} set={set} disabled={!canEdit} />
                    <NumberField label="最大绝对变更" path="bid_guardrails.max_absolute_change" strategy={strategy} set={set} disabled={!canEdit} step="0.01" suffix={scope.currency} />
                    <NumberField label="收割出价上限" path="bid_guardrails.harvest_bid_ceiling" strategy={strategy} set={set} disabled={!canEdit} step="0.01" suffix={scope.currency} />
                  </div>
                </StrategySection>

                <StrategySection icon={<Plus />} title="收割目标" description="设置搜索词和 ASIN 收割时缺失目标的创建方式。">
                  <div className="strategy-form-grid">
                    <SwitchField label="缺失目标自动创建" path="harvest_destinations.create_if_missing" strategy={strategy} set={set} disabled={!canEdit} />
                    <NumberField label="新 Campaign 日预算" path="harvest_destinations.campaign_daily_budget" strategy={strategy} set={set} disabled={!canEdit} step="0.01" suffix={scope.currency} />
                    <TextField label="新 Ad Group 名称" path="harvest_destinations.new_ad_group_name" strategy={strategy} set={set} disabled={!canEdit} hint="当前校验规则要求为 ASIN" />
                    <SelectField label="新 Ad Group 默认出价来源" path="harvest_destinations.new_ad_group_default_bid_source" strategy={strategy} set={set} disabled={!canEdit} options={[{ value: "BID_FLOOR", label: "BID_FLOOR（默认下限）" }]} />
                  </div>
                </StrategySection>

                <StrategySection icon={<ListChecks />} title="批量变更护栏" description="限制单次优化可以产生的动作数量和预算增幅。">
                  <div className="strategy-form-grid">
                    <NumberField label="单批最大动作数" path="batch_guardrails.max_actions" strategy={strategy} set={set} disabled={!canEdit} integer />
                    <NumberField label="单 Campaign 最大动作数" path="batch_guardrails.max_actions_per_campaign" strategy={strategy} set={set} disabled={!canEdit} integer />
                    <NumberField label="最大 Negative Exact" path="batch_guardrails.max_negative_exact" strategy={strategy} set={set} disabled={!canEdit} integer />
                    <NumberField label="最大 Negative Phrase" path="batch_guardrails.max_negative_phrase" strategy={strategy} set={set} disabled={!canEdit} integer />
                    <NumberField label="最大 Negative ASIN" path="batch_guardrails.max_negative_asin" strategy={strategy} set={set} disabled={!canEdit} integer />
                    <NumberField label="最大周度预计增量" path="batch_guardrails.max_projected_weekly_spend_increase" strategy={strategy} set={set} disabled={!canEdit} step="0.01" suffix={scope.currency} />
                  </div>
                </StrategySection>

                <StrategySection icon={<Clock3 />} title="观察周期" description="控制报表延迟排除、实验最早复盘和成熟复盘时间。">
                  <div className="strategy-form-grid four">
                    <NumberField label="排除报表延迟" path="observation.excluded_reporting_lag_days" strategy={strategy} set={set} disabled={!canEdit} integer suffix="天" />
                    <NumberField label="最早复盘" path="observation.earliest_review_days" strategy={strategy} set={set} disabled={!canEdit} integer suffix="天" />
                    <NumberField label="成熟复盘" path="observation.mature_review_days" strategy={strategy} set={set} disabled={!canEdit} integer suffix="天" />
                    <NumberField label="最大延长观察" path="observation.max_extension_days" strategy={strategy} set={set} disabled={!canEdit} integer suffix="天" />
                  </div>
                </StrategySection>

                <StrategySection icon={<Copy />} title="角色覆盖" description="按 Campaign 角色对目标 ACOS 和 CPA 乘数进行微调。1.00 表示沿用基础目标。">
                  <div className="role-override-table"><div className="role-override-head"><span>角色</span><span>Target ACOS 倍数</span><span>Target CPA 倍数</span></div>{ROLE_NAMES.map((role) => <div className="role-override-row" key={role}><strong>{role}</strong><NumberField compact path={`role_overrides.${role}.target_acos_multiplier`} strategy={strategy} set={set} disabled={!canEdit} step="0.05" /><NumberField compact path={`role_overrides.${role}.target_cpa_multiplier`} strategy={strategy} set={set} disabled={!canEdit} step="0.05" /></div>)}</div>
                </StrategySection>

                <StrategySection icon={<ShieldCheck />} title="保护项" description="这些词和 ASIN 会被保留，不参与自动否定或交叉收割。">
                  <div className="strategy-form-grid protected-grid">
                    <ListField label="品牌词" hint="每行一个，也可用逗号分隔" path="protected.brand_terms" strategy={strategy} set={set} disabled={!canEdit} />
                    <ListField label="核心词" hint="每行一个，也可用逗号分隔" path="protected.core_terms" strategy={strategy} set={set} disabled={!canEdit} />
                    <ListField label="保护 ASIN" hint="每行一个，也可用逗号分隔" path="protected.asins" strategy={strategy} set={set} disabled={!canEdit} />
                  </div>
                </StrategySection>

                <StrategySection icon={<ToggleLeft />} title="功能开关" description="关闭后，系统不会生成对应类型的优化动作。">
                  <div className="feature-toggle-grid">
                    <SwitchField label="允许 ACOS 驱动动作" path="features.allow_acos_driven_actions" strategy={strategy} set={set} disabled={!canEdit} />
                    <SwitchField label="允许关键词收割" path="features.allow_keyword_harvest" strategy={strategy} set={set} disabled={!canEdit} />
                    <SwitchField label="允许 ASIN 收割" path="features.allow_asin_harvest" strategy={strategy} set={set} disabled={!canEdit} />
                    <SwitchField label="允许 Negative Exact" path="features.allow_negative_exact" strategy={strategy} set={set} disabled={!canEdit} />
                    <SwitchField label="允许 Negative Phrase" path="features.allow_negative_phrase" strategy={strategy} set={set} disabled={!canEdit} />
                    <SwitchField label="允许 Negative ASIN" path="features.allow_negative_asin" strategy={strategy} set={set} disabled={!canEdit} />
                    <SwitchField label="允许自身 ASIN 交叉销售" path="features.allow_self_asin_cross_sell" strategy={strategy} set={set} disabled={!canEdit} />
                    <SwitchField label="允许暂停 Campaign" path="features.allow_campaign_pause" strategy={strategy} set={set} disabled={!canEdit} />
                    <SwitchField label="允许预算变更" path="features.allow_budget_changes" strategy={strategy} set={set} disabled={!canEdit} />
                    <SwitchField label="允许广告位变更" path="features.allow_placement_changes" strategy={strategy} set={set} disabled={!canEdit} />
                  </div>
                </StrategySection>

                <div className="strategy-form-actions"><button className="secondary-button" onClick={() => void save()} disabled={busy || !canEdit}><Save size={14} /> 保存草稿</button><span>{canEdit ? "保存后仍需校验并发布才会生效" : "只读版本不可直接修改"}</span></div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function StrategySection({ icon, title, description, children }: { icon: ReactNode; title: string; description: string; children: ReactNode }) {
  return <section className="strategy-section"><div className="strategy-section-header"><span className="strategy-section-icon">{icon}</span><div className="strategy-section-heading"><h3>{title} <HelpTip text={description} /></h3></div></div><div className="strategy-section-body">{children}</div></section>;
}

function NumberField({ label, hint, path, strategy, set, disabled, integer, step = "0.01", min, suffix, compact }: { label?: string; hint?: string; path: string; strategy: any; set: (path: string, value: unknown) => void; disabled?: boolean; integer?: boolean; step?: string; min?: string; suffix?: string; compact?: boolean }) {
  const value = pathValue(strategy, path);
  return <label className={compact ? "strategy-field compact" : "strategy-field"}>{label && <span className="strategy-field-label"><span>{label}</span>{hint && <HelpTip text={hint} />}</span>}<div className="strategy-input-wrap"><input type="number" inputMode="decimal" step={integer ? "1" : step} min={min} value={value == null ? "" : value} onChange={(event) => set(path, event.target.value === "" ? "" : integer ? Number.parseInt(event.target.value, 10) : Number(event.target.value))} disabled={disabled} />{suffix && <em>{suffix}</em>}</div></label>;
}

function formatPercentInput(raw: unknown): string {
  if (raw == null || raw === "") return "";
  const num = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(num)) return "";
  return parseFloat((num * 100).toPrecision(10)).toString();
}

function PercentField({ label, hint, path, strategy, set, disabled, nullable }: { label: string; hint?: string; path: string; strategy: any; set: (path: string, value: unknown) => void; disabled?: boolean; nullable?: boolean }) {
  const raw = pathValue(strategy, path);
  const formatted = formatPercentInput(raw);
  const [localVal, setLocalVal] = useState(formatted);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setLocalVal(formatPercentInput(raw));
    }
  }, [raw, isFocused]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setLocalVal(val);
    if (val === "") {
      set(path, nullable ? null : "");
    } else {
      const num = Number(val);
      if (!isNaN(num)) {
        set(path, parseFloat((num / 100).toPrecision(10)));
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    setLocalVal(formatPercentInput(pathValue(strategy, path)));
  };

  return (
    <label className="strategy-field">
      <span className="strategy-field-label">
        <span>{label}</span>
        {hint && <HelpTip text={hint} />}
      </span>
      <div className="strategy-input-wrap">
        <input
          type="number"
          inputMode="decimal"
          step="0.1"
          min="0"
          value={isFocused ? localVal : formatted}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          disabled={disabled}
        />
        <em>%</em>
      </div>
    </label>
  );
}

function TextField({ label, hint, path, strategy, set, disabled }: { label: string; hint?: string; path: string; strategy: any; set: (path: string, value: unknown) => void; disabled?: boolean }) {
  return <label className="strategy-field"><span className="strategy-field-label"><span>{label}</span>{hint && <HelpTip text={hint} />}</span><input type="text" value={pathValue(strategy, path) ?? ""} onChange={(event) => set(path, event.target.value)} disabled={disabled} /></label>;
}

function SelectField({ label, path, strategy, set, disabled, options }: { label: string; path: string; strategy: any; set: (path: string, value: unknown) => void; disabled?: boolean; options: Array<{ value: string; label: string }> }) {
  return <label className="strategy-field"><span>{label}</span><select value={pathValue(strategy, path) ?? ""} onChange={(event) => set(path, event.target.value)} disabled={disabled}>{options.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select></label>;
}

function ListField({ label, hint, path, strategy, set, disabled }: { label: string; hint?: string; path: string; strategy: any; set: (path: string, value: unknown) => void; disabled?: boolean }) {
  const value = pathValue(strategy, path);
  return <label className="strategy-field list-field"><span className="strategy-field-label"><span>{label}</span>{hint && <HelpTip text={hint} />}</span><textarea rows={4} value={Array.isArray(value) ? value.join("\n") : ""} onChange={(event) => set(path, event.target.value.split(/[\n,]/).map((item) => item.trim()).filter(Boolean))} disabled={disabled} /></label>;
}

function HelpTip({ text }: { text: string }) {
  return <span className="strategy-help-tip" tabIndex={0} aria-label={text}><CircleHelp size={13} /><span role="tooltip">{text}</span></span>;
}

function SwitchField({ label, path, strategy, set, disabled }: { label: string; path: string; strategy: any; set: (path: string, value: unknown) => void; disabled?: boolean }) {
  const checked = pathValue(strategy, path) === true;
  return <label className={`strategy-switch ${checked ? "checked" : ""} ${disabled ? "disabled" : ""}`}><input type="checkbox" checked={checked} onChange={(event) => set(path, event.target.checked)} disabled={disabled} /><span className="strategy-switch-track"><i /></span><span>{label}</span></label>;
}

function ValidationSummary({ validation }: { validation: any }) {
  const errors = validation.errors ?? [];
  const warnings = validation.warnings ?? [];
  return <div className={`strategy-validation ${validation.valid ? "valid" : "invalid"}`}><div>{validation.valid ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}<strong>{validation.valid ? "校验通过" : `发现 ${errors.length} 项错误`}</strong></div>{errors.slice(0, 4).map((item: any, index: number) => <span key={`error-${index}`}>{item.path ? `${item.path}：` : ""}{item.message}</span>)}{!errors.length && warnings.slice(0, 3).map((item: any, index: number) => <span key={`warning-${index}`}>提示：{item.message}</span>)}</div>;
}

function LoadingSettings() {
  return <section className="empty-state strategy-empty-state"><RefreshCw className="spin" /><h2>正在加载策略</h2><p>正在读取当前 ASIN 的策略版本。</p></section>;
}
